import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { attachmentsService, type Attachment } from '../../../services/projectManagement/attachmentsService';

interface AttachmentsPanelProps {
  taskId: string;
  onUpdate?: () => void;
}

const AttachmentsPanel: React.FC<AttachmentsPanelProps> = ({ taskId, onUpdate }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void fetchAttachments();
  }, [taskId]);

  const fetchAttachments = async () => {
    setLoading(true);
    try {
      const { data, error } = await attachmentsService.list(taskId);

      if (error) {
        toast.error('Failed to load attachments');
      } else {
        setAttachments(data || []);
      }
    } catch (err) {
      toast.error('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploading(true);
    try {
      const { error } = await attachmentsService.upload(taskId, file);

      if (error) {
        toast.error(error);
      } else {
        toast.success('File uploaded successfully');
        await fetchAttachments();
        onUpdate?.();
      }
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const { data: url, error } = await attachmentsService.getDownloadUrl(attachment.storage_path);

      if (error || !url) {
        toast.error('Failed to get download URL');
        return;
      }

      // Open in new tab
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      const { error } = await attachmentsService.delete(attachmentId);

      if (error) {
        toast.error(error);
      } else {
        toast.success('Attachment deleted');
        await fetchAttachments();
        onUpdate?.();
      }
    } catch (err) {
      toast.error('Failed to delete attachment');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="text-sm text-gray-500">Loading attachments...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">
          Attachments ({attachments.length})
        </h4>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : '📎 Add File'}
        </button>
      </div>

      {/* File size limit notice */}
      <div className="text-xs text-gray-500">
        Maximum file size: 10MB
      </div>

      {/* Attachments List */}
      {attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map((attachment) => {
            const icon = attachmentsService.getFileIcon(attachment.file_type);
            const size = attachmentsService.formatFileSize(attachment.file_size);
            const isImage = attachment.file_type.startsWith('image/');

            return (
              <div
                key={attachment.id}
                className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50"
              >
                {/* File Icon */}
                <div className="flex-shrink-0 text-2xl">
                  {icon}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => void handleDownload(attachment)}
                    className="text-left w-full"
                  >
                    <p className="text-sm font-medium text-gray-900 truncate hover:text-blue-600">
                      {attachment.file_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {size} • {formatDate(attachment.created_at)}
                      {attachment.uploader_email && ` • ${attachment.uploader_email}`}
                    </p>
                  </button>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Download Button */}
                  <button
                    onClick={() => void handleDownload(attachment)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Download"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => void handleDelete(attachment.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity"
                    title="Delete"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <div className="text-3xl mb-2">📎</div>
          <p className="text-sm text-gray-600">No attachments yet</p>
          <p className="text-xs text-gray-500 mt-1">
            Click "Add File" to upload documents
          </p>
        </div>
      )}
    </div>
  );
};

export default AttachmentsPanel;
